import {ChangeDetectorRef, Injectable, NgZone, OnDestroy} from '@angular/core';

/**
 * Coalesces ChangeDetectorRef.detectChanges() calls per cell using a single
 * requestAnimationFrame tick. Works in tandem with the @DetectMethodChanges()
 * decorator (utils.ts:410): components that inject this service as a private
 * `_panelCd` field cause the decorator to delegate here instead of calling
 * cd.detectChanges() directly.
 *
 * Pattern mirrors auditTime(0, animationFrameScheduler) used in
 * hg-hedge-matrix.component.ts for grid refreshCells batching, and the
 * NgZone.runOutsideAngular pattern in message-bus.service.ts.
 *
 * Hidden-tab handling: rAF does not fire when the tab is hidden. We accept
 * the catch-up flush on visibility resume — the dirty Set is bounded by the
 * number of live cell renderer instances (per-cell ChangeDetectorRef is the
 * key, so re-marking a dirty cell is a no-op). A single rAF flushes all
 * accumulated dirty cells in one burst when the tab becomes visible, which
 * is the desired catch-up-to-current-state behavior. No setTimeout fallback.
 */
@Injectable({providedIn: 'root'})
export class PanelChangeDetectionService implements OnDestroy {

    private readonly _dirty = new Set<ChangeDetectorRef>();
    private _rafHandle: number | null = null;

    constructor(private readonly _ngZone: NgZone) {
    }

    ngOnDestroy(): void {
        if (this._rafHandle != null) {
            cancelAnimationFrame(this._rafHandle);
            this._rafHandle = null;
        }
        this._dirty.clear();
    }

    /**
     * Marks a cell dirty. First mark in a frame schedules a single rAF; subsequent
     * marks before the rAF fires are absorbed by the Set (no duplicate work).
     */
    schedule(cd: ChangeDetectorRef): void {
        if (cd == null) { return; }
        this._dirty.add(cd);
        if (this._rafHandle != null) { return; }
        this._ngZone.runOutsideAngular(() => {
            this._rafHandle = requestAnimationFrame(() => this.flush());
        });
    }

    /**
     * Synchronous flush for interactive paths (click/keypress) where waiting for
     * the next rAF tick is perceptible. Reserved for future use — POC migration
     * does not call this.
     */
    flushNow(cd: ChangeDetectorRef): void {
        if (cd == null) { return; }
        this._dirty.delete(cd);
        try { cd.detectChanges(); } catch { /* detached/destroyed view */ }
    }

    private flush(): void {
        this._rafHandle = null;
        const batch = Array.from(this._dirty);
        this._dirty.clear();
        for (const cd of batch) {
            try { cd.detectChanges(); } catch { /* detached/destroyed view */ }
        }
    }
}
